import { Box, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useLocale } from '@/contexts/LocaleContext'
import { formatDate } from '@/utils/formatDateTime'
import type { CurrentStanding } from '@/services/billingService'

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * When the allowance runs out at the pace of this period so far, or null.
 *
 * A straight line through the period's start and today. Crude on purpose: it
 * answers "will I make it to the refill", which is the only question the
 * number is for. Nothing is projected before a day has passed or before
 * anybody has been through — one respondent in the first hour is not a pace.
 */
function exhaustionDate(
  used: number,
  limit: number,
  periodStart: string,
  periodEnd: string,
  now: number = Date.now(),
): { runsOutAt: Date | null; lastsThePeriod: boolean } | null {
  const start = Date.parse(periodStart)
  const end = Date.parse(periodEnd)
  const elapsed = now - start
  if (!Number.isFinite(start) || !Number.isFinite(end) || used <= 0 || elapsed < DAY_MS) return null
  if (used >= limit) return null
  const runsOutAt = start + (limit / used) * elapsed
  return runsOutAt >= end
    ? { runsOutAt: null, lastsThePeriod: true }
    : { runsOutAt: new Date(runsOutAt), lastsThePeriod: false }
}

/**
 * The number somebody comes to this screen for: how many respondents are
 * left, and when the allowance refills.
 *
 * Big, because it is the one figure on the page that changes from day to day.
 * The plan's name and price sit above it as a caption — they are what the
 * account is, not what it needs to know this morning.
 */
export default function UsageHero({ current }: { current: CurrentStanding }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('plans')
  const { language } = useLocale()

  const { limit, used, period_start: periodStart, period_end: periodEnd } = current.respondents
  const hasPackage = limit !== null && limit > 0
  const percent = hasPackage ? Math.min(100, (used / (limit as number)) * 100) : 0
  const exhausted = hasPackage && used >= (limit as number)
  const barColor = exhausted ? c.coralHover : c.coral

  const refillDate = periodEnd ? formatDate(periodEnd, language) : null
  const daysToRefill = periodEnd
    ? Math.max(0, Math.ceil((Date.parse(periodEnd) - Date.now()) / DAY_MS))
    : null
  const forecast =
    hasPackage && periodStart && periodEnd
      ? exhaustionDate(used, limit as number, periodStart, periodEnd)
      : null
  const trialDays = current.on_trial ? current.days_left : null

  return (
    <Box
      sx={{
        bgcolor: c.white,
        borderRadius: '20px',
        p: { xs: 2.5, md: 4 },
        boxShadow: '0 1px 2px rgba(27,28,56,.04), 0 8px 24px rgba(27,28,56,.05)',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{ justifyContent: 'space-between', alignItems: { md: 'flex-end' }, gap: 2 }}
      >
        <Box>
          <Typography
            component="p"
            sx={{
              fontFamily: theme.affexy.fonts.heading,
              fontSize: { xs: 52, md: 64 },
              fontWeight: 900,
              lineHeight: 1,
              letterSpacing: '-0.035em',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {used}
            {hasPackage && (
              <Box
                component="span"
                sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 700, color: c.ink32, letterSpacing: '-0.01em', ml: 1 }}
              >
                {t('hero.ofLimit', { limit })}
              </Box>
            )}
          </Typography>
          <Typography sx={{ mt: 1, color: c.ink56 }}>{t('standing.collected')}</Typography>
        </Box>

        <Box sx={{ textAlign: { md: 'right' } }}>
          {current.on_trial ? (
            <Typography sx={{ fontWeight: 700, color: c.coral, fontVariantNumeric: 'tabular-nums' }}>
              {trialDays === null
                ? t('standing.trialOpen')
                : trialDays <= 0
                  ? t('standing.trialToday')
                  : t('standing.trial', { count: trialDays })}
            </Typography>
          ) : refillDate ? (
            <>
              <Typography sx={{ fontWeight: 700 }}>{t('hero.renews', { date: refillDate })}</Typography>
              {hasPackage && daysToRefill !== null && (
                <Typography sx={{ fontSize: 13, color: c.ink56 }}>
                  {t('hero.refillIn', { count: daysToRefill, limit })}
                </Typography>
              )}
            </>
          ) : null}
        </Box>
      </Stack>

      {hasPackage ? (
        <>
          <Box
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={limit as number}
            aria-valuenow={used}
            sx={{ mt: 3, height: 12, borderRadius: 6, bgcolor: c.gray100, overflow: 'hidden' }}
          >
            <Box
              sx={{
                width: `${percent}%`,
                height: '100%',
                borderRadius: 6,
                background: `linear-gradient(90deg, ${c.coral300}, ${barColor})`,
                transition: 'width 400ms cubic-bezier(.2,.8,.2,1)',
              }}
            />
          </Box>
          <Stack
            direction="row"
            sx={{ mt: 2, gap: { xs: 1.5, md: 3.5 }, flexWrap: 'wrap', fontSize: 14, alignItems: 'baseline' }}
          >
            <LegendItem color={c.coral} label={t('hero.used')} value={used} />
            <LegendItem color={c.gray200} label={t('hero.left')} value={current.respondents.remaining ?? 0} />
            {exhausted ? (
              <Typography sx={{ fontSize: 14, color: c.coral, fontWeight: 700 }}>
                {t('hero.exhausted')}
              </Typography>
            ) : forecast ? (
              <Typography sx={{ fontSize: 14, color: c.ink56 }}>
                {forecast.lastsThePeriod
                  ? t('hero.forecastLasts')
                  : t('hero.forecastUntil', { date: formatDate(forecast.runsOutAt as Date, language) })}
              </Typography>
            ) : null}
          </Stack>
        </>
      ) : (
        <Typography sx={{ mt: 2, fontSize: 14, color: c.ink56 }}>
          {t(current.plan === 'facewise_enterprise' ? 'standing.contractPackageHint' : 'standing.noPackageHint')}
        </Typography>
      )}

      {!current.has_access && (
        <Box
          sx={{
            mt: 3,
            p: 1.75,
            borderRadius: '12px',
            bgcolor: alpha(c.coral, 0.05),
            border: `1px solid ${alpha(c.coral, 0.25)}`,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{t('standing.expiredTitle')}</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13.5, color: c.ink72 }}>{t('standing.expiredBody')}</Typography>
        </Box>
      )}
    </Box>
  )
}

function LegendItem({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <Typography component="span" sx={{ fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
      {label}
      <Box component="b" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Box>
    </Typography>
  )
}
